import { Component } from '@angular/core';

interface ReleaseSnapshotCard {
  label: string;
  value: string;
  helper: string;
}

interface ReleaseProgressItem {
  dateLabel: string;
  title: string;
  detail: string;
}

interface ReleaseQuickLink {
  label: string;
  route: string;
  description: string;
  iconClass: string;
}

interface ReleaseRuleRow {
  key: string;
  label: string;
  primaryRule: string;
  fallbackRule: string;
  sourceTables: string;
  notes: string;
}

interface ReleaseMetricRow {
  metricKey: string;
  label: string;
  calculation: string;
  benchmarkRule: string;
  notes: string;
}

interface ReleaseGovernanceRow {
  area: string;
  currentRule: string;
  livesIn: string;
  laterDirection: string;
}

@Component({
  selector: 'app-agent-performance-activity-metrics-progress',
  templateUrl: './agent-performance-activity-metrics-progress.component.html',
  styleUrls: ['./agent-performance-activity-metrics-progress.component.scss']
})
export class AgentPerformanceActivityMetricsProgressComponent {
  readonly asOfDate = '23 May 2026';

  readonly snapshotCards: ReleaseSnapshotCard[] = [
    {
      label: 'Release Scope',
      value: 'Stage 1 Indexed Rollout',
      helper: 'Normal admin indexed dashboards plus operator and admin evidence surfaces'
    },
    {
      label: 'Current Release State',
      value: 'Release Candidate',
      helper: 'Local truth is signed off; live monthly windows still need explicit coverage validation'
    },
    {
      label: 'Governed Truth Contract',
      value: 'Selected Window + Selected Sources',
      helper: 'Indexed pages stay on Reporting Tables, clip to the trustworthy indexed slice, and do not silently fall back to live reads'
    },
  ];

  readonly deliveredNow: ReleaseProgressItem[] = [
    {
      dateLabel: '2026-05-20',
      title: 'Indexed transition dashboards are shipped',
      detail: 'Normal admin users now have dedicated indexed-only routes for the governed Stage 1 reporting path: Attempts Dashboard Indexed 2.0 and Dashboard Indexed 2.0.'
    },
    {
      dateLabel: '2026-05-23',
      title: 'Shared indexed coverage contract is aligned across surfaces',
      detail: 'The main advanced page, indexed dashboards, and hosted job or subject evidence panels now describe selected window, effective indexed result window, and indexed coverage using the same contract.'
    },
    {
      dateLabel: '2026-05-23',
      title: 'Indexed-through truth now respects the actual latest indexed event',
      detail: 'UI indexed boundaries are no longer allowed to overstate coverage from requested sync window ends alone; they are capped by the real latest indexed event timestamp.'
    },
    {
      dateLabel: '2026-05-23',
      title: 'Hosted job and subject panels now use a calmer scoped explanation',
      detail: 'Hosted evidence panels keep the natural selected job or subject window visible, then explain which indexed slice is currently covered inside that selected scope.'
    },
    {
      dateLabel: '2026-05-22',
      title: 'Fixed-point truth is hardened beyond the original April shape',
      detail: 'Closure truth is split into explicit close, last touch, and compatibility close-or-last-touch; subject-stage truth now uses real transition evidence with log type 77 primary and 14 fallback.'
    },
    {
      dateLabel: '2026-05-20',
      title: 'Normal admin and operator surfaces are separated cleanly',
      detail: 'Indexed transition dashboards are the normal admin release path, hosted job and subject evidence panels are admin-only, and advanced sync, setup, cleanup, and diagnostics stay operator-facing.'
    },
    {
      dateLabel: '2026-05-20',
      title: 'Viewer defaults now match the indexed rollout plan',
      detail: 'The main advanced page and indexed transition dashboards default to the previous full calendar month, while hosted job and subject panels default to the natural scoped window for the selected record.'
    },
    {
      dateLabel: '2026-05-20',
      title: 'Scoped subject-family lookup performance is hardened',
      detail: 'The event store now carries the additive subject_number plus canonical_epoch_ms index needed for faster subject-family evidence lookups and hosted panel usage.'
    },
    {
      dateLabel: '2026-05-23',
      title: 'Rule and release documents are aligned with the live contract',
      detail: 'The progress view, launch notes, operator runbook, release status, workflow notes, and UI architecture docs now describe the same fixed points, fallbacks, indexed-window rules, and hosted-panel behavior.'
    },
  ];

  readonly inProgress: ReleaseProgressItem[] = [
    {
      dateLabel: '2026-05-23',
      title: 'Production backfill for wider live windows',
      detail: 'Live indexing is underway, but wider production months still need explicit reindex and coverage confirmation before they are treated as signed-off truth windows.'
    },
    {
      dateLabel: '2026-05-23',
      title: 'Larger-window production validation pack',
      detail: 'The next audit step is to run larger real production windows through the indexed dashboards and hosted evidence surfaces and confirm the same contract still holds cleanly.'
    },
    {
      dateLabel: '2026-05-23',
      title: 'Async resumable sync is still a backend follow-on',
      detail: 'Chunked sync is live and safer than before, but true persisted resume and UI polling are still not the shipped orchestration model.'
    },
    {
      dateLabel: '2026-05-23',
      title: 'Identity and attribution dimension governance',
      detail: 'Brand, client, user, user-level, and richer attribution breakdown families are still later governance work and should not be implied as fully frozen indexed dimensions yet.'
    },
  ];

  readonly knownLimits: ReleaseProgressItem[] = [
    {
      dateLabel: '2026-05-23',
      title: 'Signed-off truth is still a narrow trusted set',
      detail: 'The release is locally signed off on the trusted February validation windows, not on every future production month or every large historical range.'
    },
    {
      dateLabel: '2026-05-23',
      title: 'Async resumable sync is not shipped yet',
      detail: 'Chunk progress is safer than the original model, but there is still no true persisted resume workflow if a browser-held run dies mid-stream.'
    },
    {
      dateLabel: '2026-05-23',
      title: 'Large-window truth still depends on indexed coverage',
      detail: 'The new indexed dashboards can safely clip to the overlapping indexed slice, but no one should present an unindexed tail as if it were already governed truth.'
    },
    {
      dateLabel: '2026-05-23',
      title: 'Hosted job and subject panels are evidence helpers, not the main truth surface',
      detail: 'They are useful scoped audit views, but the indexed transition dashboards remain the governed client-facing release path.'
    },
    {
      dateLabel: '2026-05-23',
      title: 'Identity dimensions are not fully governed yet',
      detail: 'Brand, reporting brand, client, user, and wider hierarchy context are carried where available, but they are not yet frozen as the final first-class indexed dimension model across every 2.0 surface.'
    },
  ];

  readonly nextReleaseItems: ReleaseProgressItem[] = [
    {
      dateLabel: 'Next',
      title: 'Audit additional production months and wider windows',
      detail: 'Extend the signed-off proof set beyond the February local validation family and confirm the same indexed contract on larger live windows.'
    },
    {
      dateLabel: 'Next',
      title: 'Persisted resumable sync orchestration',
      detail: 'Move from browser-held chunk loops toward server-side persisted progress with polling so longer live backfills are operationally safer.'
    },
    {
      dateLabel: 'Next',
      title: 'Freeze the full identity-dimension model',
      detail: 'Promote brand, reporting brand, client, user, and user-level context into explicit governed indexed dimensions before expanding more dashboards and dynamic admin behavior.'
    },
    {
      dateLabel: 'Next',
      title: 'Attribution filters and grouped outputs',
      detail: 'Add servicing-agent, stage-actor-user, touched-by-agent, and touched-by-user breakdowns without changing the timing metric formulas already signed off.'
    },
    {
      dateLabel: 'Next',
      title: 'Raw and debug system_log hardening',
      detail: 'Keep tightening bounded log windows, caps, and operator-only diagnostics so the raw debugging surfaces do not drift into acting like unbounded report paths.'
    },
  ];

  readonly approachTaken: ReleaseProgressItem[] = [
    {
      dateLabel: '2026-05-20',
      title: 'Build beside legacy, not through it',
      detail: 'Indexed transition pages, hosted evidence panels, and operator tooling were added additively so legacy routes remain intact during rollout and rollback remains narrow.'
    },
    {
      dateLabel: '2026-05-22',
      title: 'Freeze truth before widening the surface',
      detail: 'Fixed points, fallback rules, closure behavior, instruction anchors, and hosted indexed-boundary behavior were tightened before claiming broader release readiness.'
    },
    {
      dateLabel: '2026-05-23',
      title: 'Separate selected scope from indexed coverage',
      detail: 'The user chooses the natural reporting scope, then indexed surfaces explain what trustworthy indexed slice is currently covered inside that scope instead of pretending the selected window changed.'
    },
    {
      dateLabel: '2026-05-23',
      title: 'Keep indexed truth explicit and quiet',
      detail: 'The rollout direction is no silent live fallback, no overstated indexed-through dates, and fewer noisy warning blocks in client-adjacent panels while still keeping the indexed contract explicit.'
    },
  ];

  readonly dateAndMetricLogicNow: ReleaseProgressItem[] = [
    {
      dateLabel: 'Time Rule',
      title: 'Brisbane legal time is the reporting window',
      detail: 'Metrics 2.0 admits activity using the Brisbane legal-time window selected in the UI, while storing canonical sync and event timestamps in UTC for consistent indexing and comparison.'
    },
    {
      dateLabel: 'Storage Rule',
      title: 'Canonical event time is resolved once and reused',
      detail: 'Raw source rows can arrive in different formats, but Metrics 2.0 resolves one canonical event timestamp and carries that through indexed reads, counts, KPIs, and breakdowns.'
    },
    {
      dateLabel: 'Fixed Point',
      title: 'Stage 1 timing anchors are now explicit governed fixed points',
      detail: 'The delivered fixed points are job.loaded_at, job.opened_at, job.instructions_sent_at, attempt.first_actual_at, attempt.first_agent_action_at, attempt.first_staff_recorded_at, activity.first_in_range_at, job.closed_at, job.last_touch_at, and job.closed_or_last_touch_at.'
    },
    {
      dateLabel: 'Metric Rule',
      title: 'Attempt timing metrics are calculated between fixed points and resolved attempts',
      detail: 'Current 2.0 metrics include instructions-to-first, instructions-to-second, first-to-second gap, instructions-to-third, second-to-third gap, open-to-closed-or-last-touch, plus separate support metrics for loaded/open operational lineage.'
    },
    {
      dateLabel: 'Index Rule',
      title: 'Indexed coverage is governed by selected window plus selected sources',
      detail: 'Search filters such as agent, subject, state, postcode, job type, exact outcome, service type, or resolution outcome do not narrow what gets indexed. They apply later at report time.'
    },
    {
      dateLabel: 'Boundary Rule',
      title: 'Indexed pages show the selected window and the trustworthy indexed slice separately',
      detail: 'The selected window remains visible, but indexed results clip to the overlapping indexed slice. The displayed indexed-through boundary is capped by the actual latest indexed event timestamp, not only by successful sync window metadata.'
    },
    {
      dateLabel: 'Hosted Rule',
      title: 'Hosted evidence panels keep the natural scoped window',
      detail: 'Job and subject hosted panels default to the natural scoped record window first, then explain what portion of that selected scope is currently covered by indexed Reporting Tables.'
    },
    {
      dateLabel: 'Reporting Rule',
      title: 'Reporting Tables still run KPI, rollup, and audit logic',
      detail: 'Using Reporting Tables does not skip KPI or audit logic. It switches the data source from live raw reconstruction to indexed canonical events, then builds rollups, grouped breakdowns, metrics, and audit output from that dataset.'
    },
  ];

  readonly canonicalSourceTimeRegistry: ReleaseRuleRow[] = [
    {
      key: 'subject_services',
      label: 'Attempts / Services',
      primaryRule: 'Prefer an actual attempt timestamp in this order: parsed service_local when it is a real datetime value, then date_service_local + time_service_local, then date_service + time_service_24hr, then date_service + time_service, then date_service.',
      fallbackRule: 'No staff-entry timestamp fallback is admitted for actual attendance truth.',
      sourceTables: 'subject_services',
      notes: 'This is the most important source-time rule because attempt timing, timeline truth, and first_actual fixed points depend on it. subject_services.created_date is not used as actual attendance truth.'
    },
    {
      key: 'system_log',
      label: 'System Log',
      primaryRule: 'Use created_date for the log event timestamp.',
      fallbackRule: 'No alternate event-time family is currently applied.',
      sourceTables: 'system_log',
      notes: 'Used for timeline truth, audit context, and job-opened fallback discovery.'
    },
    {
      key: 'subject_notes',
      label: 'Subject Notes',
      primaryRule: 'Use created_date.',
      fallbackRule: 'None.',
      sourceTables: 'subject_notes',
      notes: 'Pure note-created event family.'
    },
    {
      key: 'subject_communication_history',
      label: 'Communications',
      primaryRule: 'Use created_date.',
      fallbackRule: 'None.',
      sourceTables: 'subject_communication_history',
      notes: 'Communication activity stays on its own event family and does not invent attempt time.'
    },
    {
      key: 'files',
      label: 'Files / Documents',
      primaryRule: 'Use updated_date when present, otherwise created_date.',
      fallbackRule: 'created_date if updated_date is blank.',
      sourceTables: 'files',
      notes: 'Represents latest file touch activity rather than only original upload time.'
    },
    {
      key: 'jobs',
      label: 'Jobs',
      primaryRule: 'Use updated_date when present, otherwise created_date.',
      fallbackRule: 'created_date if updated_date is blank.',
      sourceTables: 'jobs',
      notes: 'Current Stage 1 job lifecycle rows are transitional anchor events, not the final explicit lifecycle family model.'
    },
    {
      key: 'subjects',
      label: 'Subjects',
      primaryRule: 'Use updated_date when present, otherwise created_date.',
      fallbackRule: 'created_date if updated_date is blank.',
      sourceTables: 'subjects',
      notes: 'Current Stage 1 subject lifecycle rows are transitional anchor events, not the final explicit lifecycle family model.'
    },
  ];

  readonly fixedPointRegistry: ReleaseRuleRow[] = [
    {
      key: 'job.loaded_at',
      label: 'Job Loaded',
      primaryRule: 'Resolve from jobs.created_date for the base subject stage.',
      fallbackRule: 'For later spawned or reference-created subject stages, reset to subjects.created_date for that subject stage.',
      sourceTables: 'jobs, subjects',
      notes: 'This is the left-hand anchor for loaded-to-open, loaded-to-first-actual, and loaded-to-closed-or-last-touch metrics. Later subject stages must not inherit the original job start forever.'
    },
    {
      key: 'job.opened_at',
      label: 'Job Opened',
      primaryRule: 'Resolve from the earliest qualifying open or create log linked to the job or subject.',
      fallbackRule: 'Fall back to the earliest staff-recorded action for the subject-stage, then to the loaded anchor only when no better open signal exists.',
      sourceTables: 'system_log, subject_services, normalized non-agent events, jobs, subjects',
      notes: 'This is the operational start point for attempt timing. It should reflect when staff actually opened or acted on the subject-stage, not simply when the job was loaded.'
    },
    {
      key: 'job.instructions_sent_at',
      label: 'Instructions Sent To Agent',
      primaryRule: 'Resolve from the earliest qualifying configured instruction-success anchor event linked to the job or subject-stage. Current active anchor: successful system_log.log_type_id = 61 Instruct Agent Communication row.',
      fallbackRule: 'Missing if no qualifying instruction dispatch log is resolved.',
      sourceTables: 'system_log',
      notes: 'This is the client-facing attendance anchor for Metrics 1, 2, and 4. The separate job instructions_date field is not used for this rule. Future log_type_id = 75 support is defined but not active.'
    },
    {
      key: 'attempt.first_actual_at',
      label: 'First Actual Attempt',
      primaryRule: 'Resolve from the earliest parsed actual attempt timestamp in subject_services.',
      fallbackRule: 'Missing if no usable subject_services attempt timestamp is resolved.',
      sourceTables: 'subject_services',
      notes: 'This is the key attempt truth anchor and depends on the subject_services time family being correct.'
    },
    {
      key: 'attempt.first_agent_action_at',
      label: 'First Agent Action',
      primaryRule: 'Resolve from the earliest attempt or normalized event attributed to an agent.',
      fallbackRule: 'Missing if no agent-attributed action is resolved.',
      sourceTables: 'subject_services, system_log, other normalized events',
      notes: 'Uses the earliest known agent footprint, not only attempt occurrence time.'
    },
    {
      key: 'attempt.first_staff_recorded_at',
      label: 'First Staff Recorded Action',
      primaryRule: 'Resolve from the earliest staff-entered subject_services created_date or other non-agent event.',
      fallbackRule: 'Missing if no staff-recorded action is resolved.',
      sourceTables: 'subject_services, normalized non-agent events',
      notes: 'This is a recorded-action timestamp, not the same thing as actual service time.'
    },
    {
      key: 'activity.first_in_range_at',
      label: 'First In-Range Activity',
      primaryRule: 'Resolve from the earliest normalized event inside the selected Brisbane legal window.',
      fallbackRule: 'Missing if no in-range activity exists.',
      sourceTables: 'all enabled normalized sources',
      notes: 'This is the admission anchor for the selected reporting window.'
    },
    {
      key: 'job.closed_at',
      label: 'Job Closed',
      primaryRule: 'Resolve from explicit close evidence only for the job or subject-stage context.',
      fallbackRule: 'Missing if no explicit close evidence exists.',
      sourceTables: 'jobs, explicit close evidence',
      notes: 'This point must not silently collapse to last touch. It represents true close evidence only.'
    },
    {
      key: 'job.last_touch_at',
      label: 'Job Last Touch',
      primaryRule: 'Resolve from the latest in-range normalized activity event for the job or subject-stage context.',
      fallbackRule: 'Missing if no in-range activity exists.',
      sourceTables: 'all enabled normalized sources',
      notes: 'This is the latest indexed activity point inside the selected reporting window, not explicit closure evidence.'
    },
    {
      key: 'job.closed_or_last_touch_at',
      label: 'Closed / Last Touch',
      primaryRule: 'Prefer job.closed_at when explicit close evidence exists.',
      fallbackRule: 'Use job.last_touch_at only when explicit close evidence is missing.',
      sourceTables: 'jobs plus all enabled normalized sources',
      notes: 'This compatibility fixed point preserves the current KPI formula while still exposing whether the result came from explicit close or last-touch fallback.'
    },
  ];

  readonly metricRegistry: ReleaseMetricRow[] = [
    {
      metricKey: 'attempt.time_instructions_to_first_actual',
      label: 'Instructions Sent To First Actual Attempt',
      calculation: 'job.instructions_sent_at -> attempt.first_actual_at',
      benchmarkRule: 'Uses legacy metric_1 benchmark from subject_service_benchmarks row 1, else 86400 seconds.',
      notes: 'Canonical client-facing Metric 1.'
    },
    {
      metricKey: 'attempt.time_instructions_to_second_actual',
      label: 'Instructions Sent To Second Actual Attempt',
      calculation: 'job.instructions_sent_at -> second resolved actual attempt timestamp.',
      benchmarkRule: 'Uses legacy metric_2 benchmark from subject_service_benchmarks row 1, else 86400 seconds.',
      notes: 'Canonical client-facing Metric 2.'
    },
    {
      metricKey: 'attempt.time_first_to_second_actual',
      label: 'First To Second Attempt Gap',
      calculation: 'attempt.first_actual_at -> second resolved actual attempt timestamp.',
      benchmarkRule: 'Uses legacy metric_3 benchmark from subject_service_benchmarks row 1, else 86400 seconds.',
      notes: 'Canonical client-facing Metric 3.'
    },
    {
      metricKey: 'attempt.time_instructions_to_third_actual',
      label: 'Instructions Sent To Third Actual Attempt',
      calculation: 'job.instructions_sent_at -> third resolved actual attempt timestamp.',
      benchmarkRule: 'Uses legacy metric_4 benchmark from subject_service_benchmarks row 1, else 86400 seconds.',
      notes: 'Canonical client-facing Metric 4.'
    },
    {
      metricKey: 'attempt.time_second_to_third_actual',
      label: 'Second To Third Attempt Gap',
      calculation: 'second resolved actual attempt timestamp -> third resolved actual attempt timestamp.',
      benchmarkRule: 'Uses legacy metric_5 benchmark from subject_service_benchmarks row 1, else 86400 seconds.',
      notes: 'Canonical client-facing Metric 5.'
    },
    {
      metricKey: 'job.time_open_to_closed_or_last_touch',
      label: 'Job Opened To Closed / Last Touch',
      calculation: 'job.opened_at -> best available closure or final touch point.',
      benchmarkRule: 'Uses legacy metric_6 benchmark from subject_service_benchmarks row 1, else 86400 seconds.',
      notes: 'Canonical client-facing Metric 6.'
    },
    {
      metricKey: 'job.time_loaded_to_open',
      label: 'Job Loaded To Open',
      calculation: 'job.loaded_at -> job.opened_at',
      benchmarkRule: 'No legacy benchmark currently attached.',
      notes: 'Pure fixed-point duration.'
    },
    {
      metricKey: 'attempt.time_open_to_first_actual',
      label: 'Job Opened To First Actual Attempt',
      calculation: 'job.opened_at -> attempt.first_actual_at',
      benchmarkRule: 'No benchmark attached.',
      notes: 'Support metric kept for operational lineage.'
    },
    {
      metricKey: 'attempt.time_loaded_to_first_actual',
      label: 'Job Loaded To First Actual Attempt',
      calculation: 'job.loaded_at -> attempt.first_actual_at',
      benchmarkRule: 'No legacy benchmark currently attached.',
      notes: 'Support metric kept for operational lineage.'
    },
    {
      metricKey: 'attempt.time_open_to_second_actual',
      label: 'Job Opened To Second Actual Attempt',
      calculation: 'job.opened_at -> second resolved actual attempt timestamp.',
      benchmarkRule: 'No benchmark attached.',
      notes: 'Support metric kept for operational lineage.'
    },
    {
      metricKey: 'attempt.time_open_to_third_actual',
      label: 'Job Opened To Third Actual Attempt',
      calculation: 'job.opened_at -> third resolved actual attempt timestamp.',
      benchmarkRule: 'No benchmark attached.',
      notes: 'Support metric kept for operational lineage.'
    },
    {
      metricKey: 'job.time_loaded_to_closed_or_last_touch',
      label: 'Job Loaded To Closed / Last Touch',
      calculation: 'job.loaded_at -> best available closure or final touch point.',
      benchmarkRule: 'No benchmark attached.',
      notes: 'Support metric kept for operational lineage.'
    },
  ];

  readonly governanceRegistry: ReleaseGovernanceRow[] = [
    {
      area: 'Audience Scope',
      currentRule: 'Current fixed points, metrics, KPI groups, and export profiles are scoped to agent reporting in Stage 1.',
      livesIn: 'activity_fixed_point_definitions, activity_metric_definitions, activity_kpi_groups, activity_export_profiles seed/config tables',
      laterDirection: 'Expand audience scopes only after the truth model is stable and non-agent formulas are explicitly defined.'
    },
    {
      area: 'Benchmark Source',
      currentRule: 'Metrics 1 to 6 benchmarks come from subject_service_benchmarks row 1. Missing values fall back to 86400 seconds.',
      livesIn: 'subject_service_benchmarks plus activity_metric_benchmarks seed rows',
      laterDirection: 'Move to governed editable benchmark management only after the delivered formulas are frozen and validated.'
    },
    {
      area: 'KPI Grouping',
      currentRule: 'Delivered KPI groups are fixed_point_timing, attempt_chain, support_timing, outcomes_and_regions, and lifecycle_and_progression.',
      livesIn: 'activity_kpi_groups and activity_kpi_group_items',
      laterDirection: 'Expose dynamic grouping management later, after user expectations and parity checks settle.'
    },
    {
      area: 'Coverage Versus Filters',
      currentRule: 'Indexed coverage is defined by selected date window plus selected activity sources only. Subject, agent, job type, outcome, state, postcode, and suburb filters apply later at report time, and partially indexed windows run only on the overlapping trustworthy indexed slice.',
      livesIn: 'Metrics 2.0 search normalization and event-store coverage logic',
      laterDirection: 'Keep this rule explicit so future dynamic UI features do not silently redefine what "indexed" means.'
    },
    {
      area: 'Release Surfaces',
      currentRule: 'Indexed transition dashboards are the normal admin release surface. Hosted job and subject Metrics 2.0 panels are admin-only evidence helpers, and advanced sync/setup/cleanup tooling remains operator-facing.',
      livesIn: 'Angular admin routes, hosted panel integration points, and existing system access control',
      laterDirection: 'Keep presentation access separate from formula governance unless user-dependent formulas are explicitly designed later.'
    },
    {
      area: 'Brand And User Dimensions',
      currentRule: 'Stage 1 reporting already carries operational agent and brand context where available, but brand type, user level, and wider brand or user hierarchy are not yet governed as full first-class indexed dimensions.',
      livesIn: 'Current event metadata, lookup hydration, brands and users tables, and route-level filters',
      laterDirection: 'Index and govern brand, brand type, user, and user-level context explicitly so all future breakdowns, KPI families, exports, and user-specific surfaces can group on the same trusted dimension model.'
    },
    {
      area: 'Indexed Identity Context',
      currentRule: 'The indexed dataset is focused on event truth, fixed points, and current Stage 1 reporting outputs. It does not yet represent the final normalized identity-context model for every user, brand, and hierarchy question.',
      livesIn: 'Canonical event rows plus runtime lookup and rollup shaping',
      laterDirection: 'Freeze the identity-dimension rules alongside canonical date rules, then promote them into seed/config and indexed rows before wider dynamic admin tooling is introduced.'
    },
    {
      area: 'Dynamic Config Boundary',
      currentRule: 'Definitions and metadata tables already exist, but formula governance is still code-led and seed-led, not a fully dynamic admin system.',
      livesIn: 'tmp/metrics_v2_helpers.php plus v2 seed/config SQL',
      laterDirection: 'Only move formula logic into dynamic admin CRUD after the canonical date and metric registries are treated as frozen truth.'
    },
  ];

  readonly quickLinks: ReleaseQuickLink[] = [
    {
      label: 'Attempts Dashboard Indexed 2.0',
      route: '/admin/system-reports/agent-performance-activity-metrics/attempts-indexed',
      description: 'Open the indexed-only normal admin attempts surface for current governed reporting.',
      iconClass: 'fa-line-chart'
    },
    {
      label: 'Dashboard Indexed 2.0',
      route: '/admin/system-reports/agent-performance-activity-metrics/dashboard-indexed',
      description: 'Open the indexed-only grouped dashboard surface used for transition rollout checks.',
      iconClass: 'fa-bar-chart'
    },
    {
      label: 'Attempts Dashboard 2.0',
      route: '/admin/system-reports/agent-performance-activity-metrics/attempts',
      description: 'Open the advanced operator-focused attempts surface with technical controls and audit widgets.',
      iconClass: 'fa-area-chart'
    },
    {
      label: 'Attempts Classic 2.0',
      route: '/admin/system-reports/agent-performance-activity-metrics/attempts-classic',
      description: 'Open the legacy-look comparison surface powered by the same v2 reporting rules.',
      iconClass: 'fa-clone'
    },
  ];

  trackByTitle(index: number, item: ReleaseProgressItem): string {
    return `${item.dateLabel}:${item.title}`;
  }

  trackByRoute(index: number, item: ReleaseQuickLink): string {
    return item.route;
  }

  trackByRuleKey(index: number, item: ReleaseRuleRow): string {
    return item.key;
  }

  trackByMetricKey(index: number, item: ReleaseMetricRow): string {
    return item.metricKey;
  }

  trackByArea(index: number, item: ReleaseGovernanceRow): string {
    return item.area;
  }
}
